import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { PsiService } from '@inversor-hub/types';
import { InterestProcessInfo } from '@/features/opportunities/InterestProcessInfo.tsx';
import { PsiServiceSelection } from '@/features/opportunities/components/PsiServiceSelection';
import { useGetPsiProfile } from '@/lib/api/psis';

interface SimpleInterestModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedServices: PsiService[]) => void;
  psiId: string;
  loading?: boolean;
}

export const SimpleInterestModal: React.FC<SimpleInterestModalProps> = ({
  open,
  onClose,
  onConfirm,
  psiId,
  loading = false,
}) => {
  const [selectedServices, setSelectedServices] = useState<PsiService[]>(['OPPORTUNITY_SEARCH']);
  const { data: psiProfile, isLoading: isLoadingPsi } = useGetPsiProfile(psiId);

  const handleServiceToggle = (service: PsiService) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedServices);
  };

  const isConfirmDisabled = selectedServices.length === 0 || loading || isLoadingPsi;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack spacing={1}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <CheckCircleOutlineIcon color="primary" />
            Confirmar interés
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Estás a punto de mostrar tu interés en esta oportunidad
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Service Selection Section */}
          <PsiServiceSelection
            psiProfile={psiProfile}
            isLoading={isLoadingPsi}
            selectedServices={selectedServices}
            onServiceToggle={handleServiceToggle}
            disabled={loading}
            showError={selectedServices.length === 0}
          />

          <InterestProcessInfo onClose={onClose} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isConfirmDisabled}
          autoFocus
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          {loading ? 'Enviando...' : 'Confirmar interés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
